// Portions of this code are adapted from the MUI Toolbar component.
// Source: https://github.com/mui/toolpad/blob/master/packages/toolpad-core/src/DashboardLayout/DashboardSidebarPageItem.tsx
// MUI is licensed under the MIT License: https://github.com/mui/toolpad/blob/master/LICENSE

import { styled, SxProps, Theme } from "@mui/material/styles";
import { Navigation, NavigationDropdown, NavigationPage } from "../../types";
import ListItemButton from "@mui/material/ListItemButton";
import { Fragment, useState } from "react";
import ListItem from "@mui/material/ListItem";
import { Link } from "react-router";
import Box from "@mui/material/Box";
import ListItemIcon from "@mui/material/ListItemIcon";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { MINI_DRAWER_WIDTH } from "../../shared";
import ListItemText from "@mui/material/ListItemText";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";
import Grow from "@mui/material/Grow";

const NavigationListItemButton = styled(ListItemButton)(({ theme }) => ({
    borderRadius: 8,
    '&.Mui-selected': {
        '& .MuiListItemIcon-root': {
            color: theme.palette.primary.dark,
        },
        '& .MuiTypography-root': {
            color: theme.palette.primary.dark,
        },
        '& .MuiSvgIcon-root': {
            color: theme.palette.primary.dark,
        },
        '& .MuiAvatar-root': {
            backgroundColor: theme.palette.primary.dark,
        },
        '& .MuiTouchRipple-child': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    '& .MuiSvgIcon-root': {
        color: theme.palette.action.active,
    },
    '& .MuiAvatar-root': {
        backgroundColor: theme.palette.action.active,
    },
}));

interface SidebarPageItemProps {
    /**
     * Unique id for the item
     */
    id: string;
    /**
     * Navigation page item definition.
     */
    item: NavigationDropdown | NavigationPage;
    /**
     * If `true`, expands any nested navigation in the item, otherwise collapse it.
     * @default false
     */
    expanded?: boolean;
    /**
     * Use to apply selected styling.
     * @default false
     */
    selected?: boolean;
    /**
     * If `true`, the item is disabled.
     * @default false
     */
    disabled?: boolean;
    /**
     * Indicates if the sidebar is a mini variant
     * @default false
     */
    isMini?: boolean;
    /**
     * Indicates if the sidebar is fully expanded
     * @default false
     */
    isFullyExpanded?: boolean;
    /**
     * Indicates if the sidebar is fully collapsed
     * @default false
     */
    isFullyCollapsed?: boolean;
    /**
     * 
     * @param {string} itemId id of the item
     * @param {NavigationDropdown | NavigationPage} item 
     * @returns 
     */
    onClick: (itemId: string, item: NavigationDropdown | NavigationPage) => void;
    /**
     * 
     * @param {Navigation} subNavigation Method to render dropdown children
     * @returns 
     */
    renderNestedNavigation: (subNavigation: Navigation) => React.ReactNode;
}

const LIST_ITEM_ICON_SIZE = 34;

function SidebarPageItem(props: SidebarPageItemProps) {
    const {
        id,
        item,
        expanded,
        selected,
        disabled,
        isMini = false,
        isFullyExpanded = false,
        isFullyCollapsed = false,
        onClick,
        renderNestedNavigation,
    } = props;

    const isDropdown = item.kind === 'dropdown';
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;

    const [hoveredMiniSidebarItemId, setHoveredMiniSidebarItemId] = useState<string | null>(null);

    // animation for expand icon
    let nestedNavigationCollapseSx: SxProps<Theme> = { display: 'none' };
    if (isMini && isFullyCollapsed) {
        nestedNavigationCollapseSx = {
            fontSize: 18,
            position: 'absolute',
            top: '41.5%',
            right: '2px',
            transform: 'translateY(-50%) rotate(-90deg)',
        };
    } else if (!isMini && isFullyExpanded) {
        nestedNavigationCollapseSx = {
            ml: 0.5,
            transform: `rotate(${expanded ? 0 : -90}deg)`,
            transition: (theme: Theme) =>
                theme.transitions.create('transform', {
                    easing: theme.transitions.easing.sharp,
                    duration: 100,
                }),
        };
    }

    const listItem = (
        <ListItem
            {...(isDropdown && {
                onMouseEnter: () => {
                    setHoveredMiniSidebarItemId(id);
                },
                onMouseLeave: () => {
                    setHoveredMiniSidebarItemId(null);
                },
            })}
            sx={{
                py: 0,
                px: 1,
                overflowX: 'hidden',
            }}
        >
            <NavigationListItemButton
                selected={selected}
                disabled={disabled}
                sx={{
                    px: 1.4,
                    height: isMini ? 60 : 48,
                }}
                LinkComponent={isDropdown ? 'button' : Link}
                {...(item.kind === 'page' && { to: item.to })}
                onClick={() => onClick(id, item)}
            >
                {/* navigation item icon */}
                {item.icon || isMini ? (
                    <Box
                        sx={{
                            ...(isMini && {
                                position: 'absolute',
                                left: '50%',
                                top: 'calc(50% - 6px)',
                                transform: 'translate(-50%, -50%)',
                            })
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: LIST_ITEM_ICON_SIZE,
                            }}
                        >
                            {item.icon ?? null}
                            {!item.icon && isMini ? (
                                <Avatar
                                    sx={{
                                        width: LIST_ITEM_ICON_SIZE - 7,
                                        height: LIST_ITEM_ICON_SIZE - 7,
                                        fontSize: 12,
                                    }}
                                >
                                    {item.title
                                        .split(' ')
                                        .slice(0, 2)
                                        .map((titleWord) => titleWord.charAt(0).toUpperCase())}
                                </Avatar>
                            ) : null}
                        </ListItemIcon>
                        {isMini ? (
                            <Typography
                                variant="caption"
                                sx={{
                                    position: 'absolute',
                                    bottom: -18,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontSize: 10,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: MINI_DRAWER_WIDTH - 28,
                                }}
                            >
                                {item.title}
                            </Typography>
                        ) : null}
                    </Box>
                ) : null}

                {/* navigation item title */}
                {!isMini ? (
                    <ListItemText
                        primary={item.title}
                        sx={{
                            ml: 1.2,
                            whiteSpace: 'nowrap',
                            zIndex: 1,
                        }}
                    />
                ) : null}

                {item.action && !isMini && isFullyExpanded ? item.action : null}
                {isDropdown && !isMini ? <ExpandMoreIcon sx={nestedNavigationCollapseSx} /> : null}
            </NavigationListItemButton>

            {/* popover for nested items when isMini is true */}
            {item.children && isMini ? (
                <Grow in={id === hoveredMiniSidebarItemId}>
                    <Box
                        sx={{
                            position: 'fixed',
                            left: MINI_DRAWER_WIDTH - 2,
                            pl: '6px',
                        }}
                    >
                        <Paper
                            sx={{
                                pt: 0.5,
                                pb: 0.5,
                                transform: 'translateY(calc(50% - 30px))',
                            }}
                        >
                            {renderNestedNavigation(item.children)}
                        </Paper>
                    </Box>
                </Grow>
            ) : null}
        </ListItem>
    );

    return (
        <Fragment key={id}>
            {listItem}
            {hasChildren && (
                <Collapse in={!isMini ? expanded : false} timeout="auto" unmountOnExit>
                    {renderNestedNavigation(item.children!)}
                </Collapse>
            )}
        </Fragment>
    );
}

export { SidebarPageItem };